// hooks/use-artifacts.js — SHARED ARTIFACT DETECTION + SAVE
// ============================================================================
// Owns: artifact detection from streamed text, saving via server-side API
// Used by: chat-feature.js, chat-space-raw.js, and any future chat files
// ============================================================================

export function useArtifacts({ userId, supabase }) {

  function detectArtifact(finalText) {
    if (!finalText) return null;

    // HTML detection — find the ```html fence and extract content up to
    // the CLOSING fence (``` on its own line). Can't use lazy regex because
    // the HTML may contain ``` inside code examples.
    const htmlFenceStart = finalText.match(/```html\s*\n/i);
    if (htmlFenceStart) {
      const startIdx = htmlFenceStart.index + htmlFenceStart[0].length;
      const afterOpen = finalText.slice(startIdx);
      // Find closing ``` on its own line (not inside the content)
      const closeIdx = afterOpen.indexOf('\n```');
      if (closeIdx >= 0) {
        const content = afterOpen.slice(0, closeIdx).trim();
        if (content.length > 0) return { content, type: 'html' };
      }
    }
    // Raw HTML (no fence) — <!DOCTYPE html>...</html>
    const rawHtml = finalText.match(/<!DOCTYPE html[\s\S]*?<\/html>/i);
    if (rawHtml) {
      return { content: rawHtml[0].trim(), type: 'html' };
    }

    // SVG detection — ONLY fenced ```svg blocks, NOT raw <svg> tags
    // (raw <svg> tags inside HTML artifacts would be false positives)
    const svgFenceStart = finalText.match(/```svg\s*\n/i);
    if (svgFenceStart) {
      const startIdx = svgFenceStart.index + svgFenceStart[0].length;
      const afterOpen = finalText.slice(startIdx);
      const closeIdx = afterOpen.indexOf('\n```');
      if (closeIdx >= 0) {
        const content = afterOpen.slice(0, closeIdx).trim();
        if (content.length > 0) return { content, type: 'svg' };
      }
    }

    // Code detection — any other fenced block
    const codeFenceStart = finalText.match(/```(\w+)\s*\n/);
    if (codeFenceStart) {
      const lang = codeFenceStart[1];
      const startIdx = codeFenceStart.index + codeFenceStart[0].length;
      const afterOpen = finalText.slice(startIdx);
      const closeIdx = afterOpen.indexOf('\n```');
      if (closeIdx >= 0) {
        const content = afterOpen.slice(0, closeIdx).trim();
        if (content.length > 0) return { content, type: lang };
      }
    }

    return null;
  }

  function normalizeArtifactType(rawType) {
    if (!rawType) return 'Document';
    const t = rawType.toLowerCase();
    if (t === 'upload') return 'Upload';
    if (t === 'html' || t === 'css') return 'Web';
    if (['javascript', 'js', 'typescript', 'ts', 'python', 'py', 'bash', 'sh',
         'ruby', 'go', 'rust', 'java', 'cpp', 'c', 'php', 'swift', 'kotlin', 'scala'].includes(t)) return 'Code';
    if (['json', 'csv', 'xml', 'yaml', 'yml', 'data', 'tsv', 'toml'].includes(t)) return 'Data';
    if (t === 'svg') return 'svg';
    if (['image', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(t)) return 'Image';
    if (['markdown', 'md', 'txt', 'text', 'document', 'prose'].includes(t)) return 'Document';
    return 'Document';
  }

  async function saveArtifact(content, type, sessionId, { skipDescription = false, messageId = null } = {}) {
    if (!userId || !content) return null;
    try {
      const normalizedType = normalizeArtifactType(type);
      const isUpload = normalizedType === 'Upload';

      // For uploads, clean filename. For code/docs, extract from content.
      let title;
      if (isUpload) {
        const rawName = decodeURIComponent((content.split('/').pop() || '').replace(/^\d+-/, '').split('?')[0]) || 'Upload';
        // Clean: remove extension, replace dashes/underscores with spaces, strip timestamps
        title = rawName
          .replace(/\.[^.]+$/, '')                    // remove extension
          .replace(/[-_]/g, ' ')                      // dashes/underscores → spaces
          .replace(/\b\d{4}[-\s]\d{2}[-\s]\d{2}\b/g, '')  // remove dates like 2024-03-15
          .replace(/\b\d{10,13}\b/g, '')              // remove unix timestamps
          .replace(/\s+/g, ' ')                       // collapse spaces
          .trim() || 'Upload';
      } else {
        title = content.match(/<title>(.*?)<\/title>/i)?.[1] || content.split('\n').find(l => l.trim())?.slice(0, 60) || 'Untitled';
      }

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const authHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

      let description = null;
      let bullets = [];

      if (isUpload) {
        // Uploads: clean metadata — AI can't see file content
        const rawName = decodeURIComponent((content.split('/').pop() || '').replace(/^\d+-/, '').split('?')[0]) || 'file';
        const ext = (rawName.split('.').pop() || '').toUpperCase();
        description = rawName;
        bullets = ext ? [`${ext} file`] : [];
      } else if (!skipDescription) {
        // Generate description via Haiku — skip for workspace artifacts
        // where speed matters more than metadata.
        try {
          const descRes = await fetch('/api/chat', {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
              action: 'generateArtifactDescription',
              content: content.slice(0, 2000),
              title: title.trim(),
              type,
              userId,
            }),
          });
          const descData = await descRes.json();
          description = descData.description || null;
          bullets = descData.bullets || [];
        } catch (_) { /* non-critical */ }
      }

      const res = await fetch('/api/user/save-artifact', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          title: title.trim(),
          content,
          artifact_type: normalizedType,
          description,
          bullets,
          session_id: sessionId || null,
          message_id: messageId || null,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        console.error('saveArtifact failed:', err);
        return null;
      }

      const { artifact } = await res.json();
      return artifact;
    } catch (e) {
      console.error('saveArtifact error:', e);
      return null;
    }
  }

  return { saveArtifact, detectArtifact };
}
